import React, { useState } from 'react';
import { Crown, Shield, UserMinus, UserPlus, Search, Check, Phone } from 'lucide-react';
import { GroupParticipant } from '../../types/groupTypes';
import { formatJidToPhone } from '../../services/groupsApi';
import { useTheme } from '../../contexts/ThemeContext';
import { formatPhone } from '../../utils/phoneUtils';

// ============================================================================
// INTERFACES
// ============================================================================

interface ParticipantListProps {
  participants: GroupParticipant[];
  onAdd?: () => void;
  onRemove?: (participants: string[]) => void;
  onPromote?: (participants: string[]) => void;
  onDemote?: (participants: string[]) => void;
  canManage?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const ParticipantList: React.FC<ParticipantListProps> = ({
  participants,
  onAdd,
  onRemove,
  onPromote,
  onDemote,
  canManage = false
}) => {
  const { theme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);

  // Filtrar participantes pela busca
  const filteredParticipants = participants.filter(p => {
    const name = p.participantName || formatJidToPhone(p.participantJid);
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Ordenar: super admins → admins → membros
  const sortedParticipants = [...filteredParticipants].sort((a, b) => {
    if (a.isSuperAdmin !== b.isSuperAdmin) return a.isSuperAdmin ? -1 : 1;
    if (a.isAdmin !== b.isAdmin) return a.isAdmin ? -1 : 1;
    return 0;
  });

  // Selecionar/desselecionar participante
  const toggleSelection = (jid: string) => {
    setSelectedParticipants(prev =>
      prev.includes(jid)
        ? prev.filter(p => p !== jid)
        : [...prev, jid]
    );
  };

  // Selecionar todos
  const selectAll = () => {
    setSelectedParticipants(sortedParticipants.map(p => p.participantJid));
  };

  // Desselecionar todos
  const deselectAll = () => {
    setSelectedParticipants([]);
  };

  // Verificar se há selecionados
  const hasSelected = selectedParticipants.length > 0;

  // Verificar se todos estão selecionados
  const allSelected = selectedParticipants.length === sortedParticipants.length && sortedParticipants.length > 0;

  return (
    <div className="space-y-4">
      {/* Header com busca e ações */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Busca */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Buscar participante..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        {/* Botão adicionar */}
        {canManage && onAdd && (
          <button
            onClick={onAdd}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
          >
            <UserPlus className="w-5 h-5" />
            <span className="hidden sm:inline">Adicionar</span>
          </button>
        )}
      </div>

      {/* Ações em lote */}
      {canManage && hasSelected && (
        <div 
          className={`border rounded-lg p-3 flex items-center justify-between ${
            theme === 'dark'
              ? 'bg-blue-900 border-blue-700'
              : 'bg-blue-50 border-blue-200'
          }`}
        >
          <span className={`text-sm font-normal ${
            theme === 'dark' ? 'text-blue-200' : 'text-blue-900'
          }`}>
            {selectedParticipants.length} selecionado(s)
          </span>
          
          <div className="flex gap-2">
            {onPromote && (
              <button
                onClick={() => {
                  onPromote(selectedParticipants);
                  deselectAll();
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm font-normal rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
              >
                <Crown className="w-4 h-4" />
                Promover
              </button>
            )}
            
            {onDemote && (
              <button
                onClick={() => {
                  onDemote(selectedParticipants);
                  deselectAll();
                }}
                className="px-3 py-1.5 bg-orange-600 text-white text-sm font-normal rounded hover:bg-orange-700 transition-colors flex items-center gap-1"
              >
                <Shield className="w-4 h-4" />
                Rebaixar
              </button>
            )}
            
            {onRemove && (
              <button
                onClick={() => {
                  if (window.confirm(`Remover ${selectedParticipants.length} participante(s)?`)) {
                    onRemove(selectedParticipants);
                    deselectAll();
                  }
                }}
                className="px-3 py-1.5 bg-red-600 text-white text-sm font-normal rounded hover:bg-red-700 transition-colors flex items-center gap-1"
              >
                <UserMinus className="w-4 h-4" />
                Remover
              </button>
            )}
          </div>
        </div>
      )}

      {/* Contador e seleção */}
      <div className="flex items-center justify-between text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
        <span>
          {sortedParticipants.length} de {participants.length} participante(s)
        </span>
        
        {canManage && sortedParticipants.length > 0 && (
          <button
            onClick={allSelected ? deselectAll : selectAll}
            className="font-normal"
            style={{
              color: theme === 'dark' ? '#60a5fa' : '#2563eb'
            }}
          >
            {allSelected ? 'Desselecionar todos' : 'Selecionar todos'}
          </button>
        )}
      </div>

      {/* Lista de participantes */}
      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {sortedParticipants.length === 0 ? (
          <div className="text-center py-8 text-sm font-normal" style={{ color: 'var(--text-muted)' }}>
            {searchTerm ? 'Nenhum participante encontrado' : 'Nenhum participante'}
          </div>
        ) : (
          sortedParticipants.map((participant) => {
            const isSelected = selectedParticipants.includes(participant.participantJid);
            const displayName = participant.participantName || formatJidToPhone(participant.participantJid);
            
            return (
              <div
                key={participant.id}
                onClick={() => canManage && toggleSelection(participant.participantJid)}
                className={`
                  flex items-center justify-between p-3 rounded-lg border transition-all
                  ${isSelected 
                    ? 'border-blue-500 ' + (theme === 'dark' ? 'bg-blue-900' : 'bg-blue-50')
                    : theme === 'dark' 
                      ? 'border-gray-700 hover:border-gray-600'
                      : 'border-gray-200 hover:border-gray-300'
                  }
                  ${canManage ? 'cursor-pointer' : ''}
                `}
                style={{
                  backgroundColor: isSelected 
                    ? undefined 
                    : 'var(--bg-card)'
                }}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {/* Checkbox (apenas se canManage) */}
                  {canManage && (
                    <div className={`
                      w-5 h-5 border-2 rounded flex items-center justify-center flex-shrink-0
                      ${isSelected 
                        ? 'border-blue-500 bg-blue-500' 
                        : theme === 'dark'
                          ? 'border-gray-600'
                          : 'border-gray-300'
                      }
                    `}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  )}

                  {/* Avatar */}
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {displayName.charAt(0).toUpperCase()}
                  </div>

                  {/* Nome e telefone */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-sm font-normal truncate" style={{ color: 'var(--text-primary)' }}>
                      {displayName}
                    </p>
                    
                    {/* Telefone */}
                    {participant.phoneNumber ? (
                      <div className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} />
                        <span className="text-sm font-normal truncate" style={{ color: 'var(--text-secondary)' }}>
                          {formatPhone(participant.phoneNumber)}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs font-normal italic truncate" style={{ color: 'var(--text-muted)' }}>
                        Telefone privado
                      </p>
                    )}
                  </div>

                  {/* Badges de status */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {participant.isSuperAdmin && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-normal rounded-full ${
                        theme === 'dark'
                          ? 'bg-yellow-900 text-yellow-200'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        <Crown className="w-3 h-3" />
                        Criador
                      </span>
                    )}
                    
                    {participant.isAdmin && !participant.isSuperAdmin && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-normal rounded-full ${
                        theme === 'dark'
                          ? 'bg-blue-900 text-blue-200'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        <Shield className="w-3 h-3" />
                        Admin
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ParticipantList;

